import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '../../lib/utils';
import { Tooltip } from '../TovTooltip/TovTooltip';
import { TovIconButton } from '../TovButton/TovButton';

function resolveStringValue(value: ReactNode) {
  return typeof value === 'string' ? value : undefined;
}

export function Label({
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn('tov-label', className)} {...props} />;
}

export function Field({
  className,
  ...props
}: HTMLAttributes<HTMLLabelElement>) {
  return <label className={cn('tov-field', className)} {...props} />;
}

export function FieldHint({
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn('tov-field__hint', className)} {...props} />;
}

export function FieldError({
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn('tov-field__error', className)} {...props} />;
}

export interface TovFieldProps extends HTMLAttributes<HTMLLabelElement> {
  label?: ReactNode;
  hint?: ReactNode;
  help?: ReactNode;
  children: ReactNode;
}

export function TovField({
  className,
  help,
  label,
  hint,
  children,
  ...props
}: TovFieldProps) {
  const helpLabel = resolveStringValue(help);

  return (
    <Field className={cn('tov-field', className)} {...props}>
      {label ? (
        <span className="tov-field__label-row">
          <Label className="tov-field__label">{label}</Label>
          {help ? (
            <Tooltip content={help}>
              <TovIconButton
                aria-label={helpLabel}
                className="tov-field__help-trigger"
                icon="question"
              />
            </Tooltip>
          ) : null}
        </span>
      ) : null}
      <span className="tov-field__control">{children}</span>
      {hint ? <FieldHint className="tov-field__hint">{hint}</FieldHint> : null}
    </Field>
  );
}
